// src/services/sessionService.js
import { supabase } from './supabaseClient';

// --- Helpers locales ---
function saveSession(user) {
  localStorage.setItem('user', JSON.stringify(user));
}

export function getCurrentUser() {
  const userJson = localStorage.getItem('user');
  if (!userJson) return null;
  try {
    return JSON.parse(userJson);
  } catch {
    return null;
  }
}

export function getUserRole() {
  const user = getCurrentUser();
  return user?.rol ?? null;
}

export function getEmpresaId() {
  const user = getCurrentUser();
  return user?.empresa_id ?? null;
}

/**
 * Borra almacenamiento local y cierra sesión en Supabase.
 */
export async function logout() {
  localStorage.removeItem('user');
  await supabase.auth.signOut();
}

/**
 * Carga el perfil extendido desde tu tabla `usuarios`
 * y fusiona con los datos base de Supabase (id, email).
 */
export async function loadAndCacheUser(sessionUser) {
  if (!sessionUser?.id) {
    localStorage.removeItem('user');
    return null;
  }

  const { data: perfil, error } = await supabase
    .from('usuarios')
    .select('id, id_auth, nombre, rol, empresa_id, activo, email')
    .eq('id_auth', sessionUser.id)
    .eq('activo', true)
    .maybeSingle();

  if (error) {
    console.error('❌ Error cargando perfil de usuario:', error);

    // Si Supabase no responde por un problema temporal de red,
    // conserva el último perfil válido asociado a esta sesión.
    const usuarioCache = getCurrentUser();

    if (usuarioCache?.id_auth === sessionUser.id) {
      return usuarioCache;
    }

    return null;
  }

  if (!perfil) {
    localStorage.removeItem('user');
    return null;
  }

  const usuarioCache = getCurrentUser();
  let empresa_nombre = usuarioCache?.empresa_nombre || '';

  if (perfil.empresa_id) {
    const { data: empresa, error: empresaError } = await supabase
      .from('empresas')
      .select('nombre')
      .eq('id', perfil.empresa_id)
      .maybeSingle();

    if (empresaError) {
      console.error('❌ Error cargando empresa:', empresaError);
    }

    if (empresa?.nombre) {
      empresa_nombre = empresa.nombre;
    }
  }

  const usuarioFinal = {
    id: perfil.id,
    id_auth: perfil.id_auth,
    nombre: perfil.nombre,
    email: perfil.email || sessionUser.email,
    rol: perfil.rol,
    empresa_id: perfil.empresa_id,
    empresa_nombre,
  };

  saveSession(usuarioFinal);

  return usuarioFinal;
  }

/**
 * Llama esto una vez al arrancar la app para hidratar desde Supabase
 * en caso de que el usuario ya tenga una sesión válida.
 */
export async function hydrateSessionFromSupabase() {
  const { data, error } = await supabase.auth.getSession();

  if (error || !data?.session?.user) {
    localStorage.removeItem('user');
    return null;
  }

  return await loadAndCacheUser(data.session.user);
}

/**
 * Activa un listener para que cada vez que cambie la sesión de Supabase
 * (login, logout, refresh token, password recovery), sincronicemos localStorage.
 */
export function initAuthListener() {
  const { data: subscription } = supabase.auth.onAuthStateChange(
    async (event, session) => {
      // console.log('Auth event:', event);
      if (session?.user) {
        await loadAndCacheUser(session.user);
      } else {
        localStorage.removeItem('user');
      }
    }
  );
  return subscription;
}